import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import MainLayout from "@/layouts/MainLayout";
import Dashboard from "@/pages/Dashboard";
import ProjectsPage from "@/pages/ProjectsPage";
import DeletedProjects from "@/pages/DeletedProjects";
import ProjectWorkspace from "@/pages/project/ProjectWorkspace";
import TasksTab from "@/pages/project/TasksTab";
import EstimateTab from "@/pages/project/EstimateTab";
import FinancialsTab from "@/pages/project/FinancialsTab";
import VariationsTab from "@/pages/project/VariationsTab";
import PurchaseOrdersTab from "@/pages/project/PurchaseOrdersTab";
import HsTab from "@/pages/project/HsTab";
import SubcontractorsTab from "@/pages/project/SubcontractorsTab";
import LabourSummaryTab from "@/pages/project/LabourSummaryTab";
import MaterialsPage from "@/pages/project/MaterialsPage";
import ProjectBills from "@/pages/project/ProjectBills";
import ProjectHours from "@/pages/project/ProjectHours";
import ProjectReport from "@/pages/project/ProjectReport";
import ProjectMore from "@/pages/project/ProjectMore";
import CostReportPage from "@/pages/CostReportPage";
import OperationsPage from "@/pages/OperationsPage";
import WorksProgramme from "@/pages/WorksProgramme";
import Reports from "@/pages/Reports";
import Configuration from "@/pages/Configuration";
import MorePage from "@/pages/MorePage";
import WorkerTimesheets from "@/pages/WorkerTimesheets";
import WorkerLeave from "@/pages/WorkerLeave";
import WorkerPayslips from "@/pages/WorkerPayslips";
import WorkerProjects from "@/pages/WorkerProjects";
import WorkerHs from "@/pages/WorkerHs";
import PublicVariationApproval from "@/pages/PublicVariationApproval";
import DocumentsTab from "@/pages/project/DocumentsTab";
import ProgrammeTab from "@/pages/project/ProgrammeTab";
import ChargeUpTab from "@/pages/project/ChargeUpTab";
import HsExtrasTab from "@/pages/project/HsExtrasTab";
import OnboardingForm from "@/pages/OnboardingForm";
import PublicDocumentView from "@/pages/PublicDocumentView";
import SiteSignIn from "@/pages/site/SiteSignIn";
import SiteCodeEntry from "@/pages/site/SiteCodeEntry";
import HsAdmin from "@/pages/hs/HsAdmin";
import NotFound from "@/pages/NotFound";
import { Toaster } from "@/components/ui/sonner";

function RouteGuard({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-black text-silver p-6">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to={user.role === "Worker" ? "/my/timesheets" : "/"} replace />;
  return children;
}

// Workers with an unfinished onboarding profile are sent to the form first.
function OnboardingGate({ children }) {
  const { user } = useAuth();
  const isWorker = user?.role === "Worker";
  const [state, setState] = useState(isWorker ? "checking" : "ok");
  useEffect(() => {
    if (!isWorker) { setState("ok"); return; }
    api.get("/me/onboarding").then(r => setState(r.data.complete ? "ok" : "todo")).catch(() => setState("ok"));
  }, [isWorker]);
  if (state === "checking") return <div className="min-h-screen bg-black text-silver p-6">Loading…</div>;
  if (state === "todo") return <Navigate to="/onboarding" replace />;
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "Worker") return <Navigate to="/my/timesheets" replace />;
  return <Dashboard />;
}

class ErrorBoundary extends React.Component {
  constructor(p){ super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err){ return { err }; }
  componentDidCatch(err, info){ console.error(err, info); }
  render(){
    if (this.state.err) return (
      <div className="min-h-screen bg-black text-white p-6 flex flex-col items-center justify-center gap-3" data-testid="error-boundary">
        <div className="text-silver text-2xl">Something went wrong</div>
        <div className="opacity-70 text-sm">{String(this.state.err?.message||this.state.err)}</div>
        <button className="btn-silver" onClick={()=>{ this.setState({err:null}); window.location.reload(); }}>Retry</button>
      </div>
    );
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage/>} />
            <Route path="/change-password" element={<RouteGuard><ChangePasswordPage/></RouteGuard>} />
            <Route path="/v/:token" element={<PublicVariationApproval/>} />
            <Route path="/d/:token" element={<PublicDocumentView/>} />
            <Route path="/qr/:code" element={<SiteSignIn/>} />
            <Route path="/site" element={<SiteCodeEntry/>} />
            <Route path="/onboarding" element={<RouteGuard><OnboardingForm/></RouteGuard>} />
            <Route element={<RouteGuard roles={["Admin","Manager"]}><MainLayout/></RouteGuard>}>
              <Route index element={<RoleRedirect/>} />
              <Route path="projects" element={<ProjectsPage/>} />
              <Route path="projects/deleted" element={<RouteGuard roles={["Admin"]}><DeletedProjects/></RouteGuard>} />
              <Route path="projects/:pid" element={<ProjectWorkspace/>}>
                <Route index element={<TasksTab/>} />
                <Route path="estimate" element={<EstimateTab/>} />
                <Route path="materials" element={<MaterialsPage/>} />
                <Route path="labour" element={<LabourSummaryTab/>} />
                <Route path="bills" element={<ProjectBills/>} />
                <Route path="hours" element={<ProjectHours/>} />
                <Route path="report" element={<ProjectReport/>} />
                <Route path="more" element={<ProjectMore/>} />
                <Route path="financials" element={<FinancialsTab/>} />
                <Route path="variations" element={<VariationsTab/>} />
                <Route path="pos" element={<PurchaseOrdersTab/>} />
                <Route path="subcontractors" element={<SubcontractorsTab/>} />
                <Route path="hs" element={<HsTab/>} />
                <Route path="hs-extras" element={<HsExtrasTab/>} />
                <Route path="documents" element={<DocumentsTab/>} />
                <Route path="programme" element={<ProgrammeTab/>} />
                <Route path="chargeup" element={<ChargeUpTab/>} />
                <Route path="*" element={<Navigate to="" replace />} />
              </Route>
              <Route path="operations/*" element={<OperationsPage/>} />
              <Route path="cost-report" element={<CostReportPage/>} />
              <Route path="programme" element={<WorksProgramme/>} />
              <Route path="reports" element={<Reports/>} />
              <Route path="hs" element={<HsAdmin/>} />
              <Route path="configuration" element={<RouteGuard roles={["Admin"]}><Configuration/></RouteGuard>} />
              <Route path="more" element={<MorePage/>} />
              {/* In-layout catch-all so unknown deep paths show NotFound, not blank */}
              <Route path="*" element={<NotFound/>} />
            </Route>
            {/* Worker-accessible routes inside MainLayout so the mobile tab bar is available */}
            <Route element={<RouteGuard roles={["Admin","Manager","Worker"]}><OnboardingGate><MainLayout/></OnboardingGate></RouteGuard>}>
              <Route path="my/timesheets" element={<WorkerTimesheets/>} />
              <Route path="my/leave" element={<WorkerLeave/>} />
              <Route path="my/payslips" element={<WorkerPayslips/>} />
              <Route path="my/projects" element={<RouteGuard roles={["Worker"]}><WorkerProjects/></RouteGuard>} />
              <Route path="my/hs" element={<RouteGuard roles={["Worker"]}><WorkerHs/></RouteGuard>} />
              <Route path="worker/more" element={<Navigate to="/my/projects" replace />} />
            </Route>
            <Route path="*" element={<NotFound/>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
